import * as React from 'react'
import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { EntityDto } from '../../config/appConfig'
import BaseOverview, { BaseOverviewCount, BaseOverviewProps } from '../BaseOverview/BaseOverview'
import { BaseOverviewFilterType } from '../BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import HammockFormDialog, { HammockFormDialogProps } from '../../v2/components/Dialog/HammockFormDialog'
import { FormikConfig } from 'formik/dist/types'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'
import { generatePath } from 'react-router'

/**
 * Basis for overview page props. As little as possible is required.
 */
export type BaseMutableOverviewProps<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
    ActionButtonPropsT = any,
    DialogBodyEntityT = any,
    DialogBodyPropsT extends BaseMutableOverviewDialogProps<EntityT> = BaseMutableOverviewDialogProps<EntityT>,
> = BaseOverviewProps<EntityT, FilterT, SortingT, HeaderT, ActionButtonPropsT> & {
    mutationDialogProps?: Omit<HammockFormDialogProps<DialogBodyEntityT, DialogBodyPropsT>, 'dialogBody' | 'dialogFormBody'>
    /**
     * Builds the form configuration for the dialog. The item is optional on purpose: it is absent while adding, and
     * also on the last render before the dialog unmounts, so every implementation has to handle that (HAM-4305).
     */
    mutationDialogFormProps?: (item?: EntityT) => FormikConfig<DialogBodyEntityT>
    mutationDialogBody: FunctionComponent<HammockFormDialogProps<DialogBodyEntityT, DialogBodyPropsT>>
    /** Same as {@link mutationDialogFormProps}: the item can be absent. */
    mutationDialogBodyProps?: (item?: EntityT) => DialogBodyPropsT
    openMutationDialog?: boolean
    setOpenMutationDialog?: (open: boolean) => void
    /**
     * Where to return to when the dialog closes after it was opened through the route. Needed for overviews whose edit
     * route carries an id (/task/:id/edit): that path cannot be regenerated with the parameters stripped (HAM-4275).
     */
    mutationDismissUrl?: string
}

export type MutationRouteMatchProps = { action?: MutationAction; id?: string }

export enum MutationAction {
    ADD = 'add',
    EDIT = 'edit',
}

export type BaseMutableOverviewDialogProps<EntityT extends EntityDto> = {
    id?: string
    item?: EntityT
    // Called by dialog bodies with the current form values; a returned message is shown as a warning MessageBar.
    formWarning?: (values: any) => string | undefined
}

export type BaseMutableOverviewActionButtonProps<EntityT> = {
    openMutationDialog?: boolean
    setOpenMutationDialog?: (open: boolean, item?: EntityT) => void
}

/**
 * This Base Overview adds a HammockFormDialog to the BaseOverview component to (for example) allow items in the list to be edited or items to be added to the list.
 *
 * @param props see {@link BaseMutableOverviewProps} for more information on which fields to include.
 */
export default function BaseMutableOverview<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
    ActionButtonProps = BaseMutableOverviewActionButtonProps<EntityT>,
    DefaultFilterTagsPropsT = object,
    DialogBodyEntityT = object,
    DialogBodyPropsT extends BaseMutableOverviewDialogProps<EntityT> = BaseMutableOverviewDialogProps<EntityT>,
>(props: BaseMutableOverviewProps<EntityT, FilterT, SortingT, HeaderT, ActionButtonProps, DialogBodyEntityT, DialogBodyPropsT>) {
    const history = useHistory()
    const pathParams = useParams<MutationRouteMatchProps>()
    const { path: currentPath, params: routeParams } = useRouteMatch<MutationRouteMatchProps>()

    const [clickedItem, setClickedItem] = useState<EntityT>()
    const [_openMutationDialog, _setOpenMutationDialog] = useState(false)

    const openMutationDialog = useMemo(() => props.openMutationDialog ?? _openMutationDialog, [props.openMutationDialog, _openMutationDialog])
    const setOpenMutationDialog = (open: boolean) => {
        if (props.setOpenMutationDialog) {
            props.setOpenMutationDialog(open)
        }
        _setOpenMutationDialog(open)
    }

    // The route opens the dialog: /<entity>/add to add, /<entity>/<id>/edit to edit a specific item (deep link).
    const routeEditId = useMemo(() => (routeParams.action === MutationAction.EDIT ? routeParams.id : undefined), [routeParams.action, routeParams.id])

    useEffect(() => {
        // A row that was clicked earlier stays in state, because closing with Escape does not run onDismiss. Clear it
        // when the route asks for something else, or /<entity>/add would reopen that row's edit dialog (HAM-4275).
        if (routeParams.action === MutationAction.ADD || (routeParams.action === MutationAction.EDIT && routeParams.id !== clickedItem?.id)) {
            setClickedItem(undefined)
        }

        setOpenMutationDialog(routeParams.action === MutationAction.ADD || routeParams.action === MutationAction.EDIT)
    }, [routeParams.action, routeParams.id])

    const [reloadTrigger, setReloadTrigger] = useState<number>(0)
    const reload = () => {
        setReloadTrigger(reloadTrigger + 1)
    }

    return (
        <BaseOverview<EntityT, FilterT, SortingT, HeaderT, ActionButtonProps, DefaultFilterTagsPropsT>
            onItemClicked={(itemProps, event) => {
                props.onItemClicked?.(itemProps, event)
                setClickedItem(itemProps.item)
                setOpenMutationDialog(true)
            }}
            {...props}
            headerActionsButtonsProps={{
                ...props.headerActionsButtonsProps,
                openMutationDialog,
                setOpenMutationDialog: (open: boolean, item?: EntityT) => {
                    if (item !== undefined) {
                        setClickedItem(item)
                    }
                    setOpenMutationDialog(open)
                },
            }}
            // Combine the internal dialog reload with a parent-driven trigger; either bump reloads.
            reloadTrigger={(Number(props.reloadTrigger) || 0) + reloadTrigger}
            focusQueryOnRender={props.focusQueryOnRender !== true && !routeParams?.action}
        >
            <HammockFormDialog<Partial<DialogBodyEntityT>, DialogBodyPropsT>
                open={openMutationDialog}
                setOpen={setOpenMutationDialog}
                {...props.mutationDialogProps}
                dialogFormProps={() => props.mutationDialogFormProps?.(clickedItem)}
                dialogFormBody={props.mutationDialogBody}
                dialogFormBodyProps={() => ({ id: clickedItem?.id ?? routeEditId, ...props.mutationDialogBodyProps?.(clickedItem) })}
                onDismiss={() => {
                    setOpenMutationDialog(false)
                    setClickedItem(undefined)
                    // Only clean up the url when the dialog is actually part of it; an overview embedded in another
                    // screen (the task widgets on the dashboard) must not navigate away when its dialog closes.
                    if (routeParams.action) {
                        history.push(props.mutationDismissUrl ?? generatePath(currentPath, { ...pathParams, action: undefined }))
                    }
                    reload()
                }}
            />
        </BaseOverview>
    )
}
